/**
 * Word Count Display Component
 * 
 * Displays word count for proposals with limit indicators and warnings
 */

import React from 'react';
import { Badge } from '@tracekit/core';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';

interface WordCountDisplayProps {
  wordCount: number;
  wordLimit?: number;
  sectionWordCounts?: Record<string, number>;
  sectionLimits?: Record<string, number>;
  showSectionBreakdown?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function WordCountDisplay({
  wordCount,
  wordLimit,
  sectionWordCounts,
  sectionLimits,
  showSectionBreakdown = false,
  size = 'md',
}: WordCountDisplayProps) {
  const isOverLimit = wordLimit ? wordCount > wordLimit : false;
  const isApproachingLimit = wordLimit ? wordCount > wordLimit * 0.9 : false;
  
  const getStatusColor = () => {
    if (isOverLimit) return 'text-red-600 dark:text-red-400';
    if (isApproachingLimit) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };
  
  const getStatusBadge = () => {
    if (isOverLimit) {
      return (
        <Badge variant="destructive" className="ml-2">
          <AlertCircle className="h-3 w-3 mr-1" />
          Over Limit
        </Badge>
      );
    }
    if (isApproachingLimit) {
      return (
        <Badge variant="warning" className="ml-2">
          <AlertCircle className="h-3 w-3 mr-1" />
          Approaching Limit
        </Badge>
      );
    }
    if (wordLimit && wordCount <= wordLimit) {
      return (
        <Badge variant="default" className="ml-2 bg-green-500">
          <CheckCircle className="h-3 w-3 mr-1" />
          Within Limit
        </Badge>
      );
    }
    return null;
  };
  
  const textSizeClass = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  }[size];
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <FileText className={`h-4 w-4 ${getStatusColor()}`} />
        <span className={`font-semibold ${textSizeClass} ${getStatusColor()}`}>
          {wordCount.toLocaleString()} words
        </span>
        {wordLimit && (
          <span className={`text-sm ${getStatusColor()}`}>
            / {wordLimit.toLocaleString()}
          </span>
        )}
        {getStatusBadge()}
      </div>
      
      {wordLimit && (
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              isOverLimit
                ? 'bg-red-500'
                : isApproachingLimit
                ? 'bg-yellow-500'
                : 'bg-green-500'
            }`}
            style={{
              width: `${Math.min(100, (wordCount / wordLimit) * 100)}%`,
            }}
          />
        </div>
      )}
      
      {showSectionBreakdown && sectionWordCounts && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Section Breakdown:
          </p>
          <div className="space-y-1">
            {Object.entries(sectionWordCounts).map(([section, count]) => {
              const sectionLimit = sectionLimits?.[section];
              const isSectionOver = sectionLimit ? count > sectionLimit : false;
              
              return (
                <div
                  key={section}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="text-gray-600 dark:text-gray-400 truncate flex-1">
                    {section}
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        isSectionOver
                          ? 'text-red-600 dark:text-red-400 font-semibold'
                          : 'text-gray-700 dark:text-gray-300'
                      }
                    >
                      {count.toLocaleString()}
                    </span>
                    {sectionLimit && (
                      <span className="text-gray-500 dark:text-gray-500">
                        / {sectionLimit.toLocaleString()}
                      </span>
                    )}
                    {isSectionOver && (
                      <AlertCircle className="h-3 w-3 text-red-500" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Calculate word count from report sections
 */
export function calculateReportWordCount(sections: any[]): number {
  if (!sections || sections.length === 0) return 0;
  
  return sections.reduce((total, section) => {
    const answer = section.answer || '';
    // Simple word count - split on whitespace and filter empty strings
    const words = answer.split(/\s+/).filter((w: string) => w.length > 0);
    return total + words.length;
  }, 0);
}

/**
 * Calculate word counts by section
 */
export function calculateSectionWordCounts(sections: any[]): Record<string, number> {
  if (!sections || sections.length === 0) return {};
  
  const counts: Record<string, number> = {};
  
  sections.forEach(section => {
    const sectionName = section.section_name || 'Unknown';
    const answer = section.answer || '';
    const words = answer.split(/\s+/).filter((w: string) => w.length > 0);
    counts[sectionName] = words.length;
  });
  
  return counts;
}
